import { defineStore, acceptHMRUpdate } from 'pinia'
import { DateTime, Duration } from 'luxon'
import type { ApiError } from '@/api/client'
import { EventsService, DeviceType } from '@/api/client'
import { useAuthStore } from '@/stores/AuthStore'
import type { Event as TServerEvent } from '@/api/client/models/Event'
import type { TDehydratedEvent } from '@/classes/Event'
import { Event } from '@/classes/Event'
import { WorkdaysService } from '@/api/client'
import { Task } from '@/classes/Task'
import { useLocationStore } from './LocationStore'
import { useEmployeeStore } from '@/stores/EmployeeStore'
import type { Employee } from '@/classes/Employee'
import { storeToRefs } from 'pinia'
import { useProjectStore } from './ProjectStore'
import { animationInterval } from '@/helpers/animationInterval'
import { ref } from 'vue'
import handleRequest from '@/helpers/handleRequest'
import type { Site } from '@/api/client/'
import { useConfigStore } from './ConfigStore'
import { useTaskStore } from './TaskStore'
import { useClockReminderStore } from './ClockReminderStore'
import { report } from '@/classes/manager/ExceptionReportingManager'

export const now = ref(DateTime.now())
let nowIntervalInitialized = false
const abortController = new AbortController()

type TWorkday = {
  date: string
  events: Event[]
  work_seconds: number
  break_seconds: number
  target_seconds: number | null
  absence_seconds?: number
  absence_categories?: string[]
  starts_at: string
  ends_at: string | null
  employee_id?: number
}

type tStateOfEmployee = {
  workdays: Array<TWorkday>
}

type TState = {
  isSyncedWithServer: boolean // remove? cant we just check all transmitted?
  employees: {
    [key: string]: tStateOfEmployee
  }
  teamWorkday: TWorkday | null // contains the combined events of all team members between the first member clocking in and the last member clocking out
}

const initialState: TState | undefined = {
  isSyncedWithServer: false,
  employees: {},
  teamWorkday: null,
}

function workdayIsTransmitted(day: TWorkday): boolean {
  return day.events.filter((x) => !x.transmitted).length === 0
}

function createInitialStateOfEmployee(): tStateOfEmployee {
  return {
    workdays: [],
  }
}

const useEventStore = defineStore('eventStore', {
  state: () => {
    if (!nowIntervalInitialized) {
      animationInterval(1000, abortController.signal, () => {
        now.value = DateTime.now()
      })
      nowIntervalInitialized = true
    }

    return { ...initialState }
  },

  getters: {
    lastWorkdayOfEmployee: (state) => {
      return (employee_id?: number): TWorkday | undefined => {
        employee_id = employee_id ?? useEmployeeStore().employee?.id
        if (!employee_id) {
          return
        }
        const workdays = state.employees[employee_id]?.workdays
        if (workdays && workdays.length) return workdays[workdays.length - 1]
      }
    },
    lastWorkday(): TWorkday | undefined {
      return this.lastWorkdayOfEmployee()
    },
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    lastWordayWorkDurationOfEmployee: (state) => {
      return (employee_id?: number): Duration => {
        const lastWorkdayOfEmployee =
          useEventStore().lastWorkdayOfEmployee(employee_id)
        if (!lastWorkdayOfEmployee) {
          return Duration.fromMillis(0)
        }
        const lastWorkdayWorkDuration = Duration.fromMillis(
          lastWorkdayOfEmployee.work_seconds * 1000,
        )
        const lastEventOfWorkday =
          useEventStore().lastEventOfEmployee(employee_id)
        if (
          lastEventOfWorkday?.task_id &&
          !Task.taskIsWork(lastEventOfWorkday?.task_id)
        ) {
          return lastWorkdayWorkDuration
        }
        return lastWorkdayWorkDuration.plus(
          useEventStore().lastEventDurationOfEmployee(employee_id),
        )
      }
    },
    lastWorkdayWorkDuration(): Duration {
      return this.lastWordayWorkDurationOfEmployee()
    },
    workday: (state) => {
      return (date: string, employee_id?: number): TWorkday | undefined => {
        employee_id = employee_id ?? useEmployeeStore().employee?.id
        if (!employee_id) {
          return
        }
        const workdays = state.employees[employee_id]?.workdays
        if (!workdays) {
          return undefined
        }
        return workdays.find((workday) => workday.date === date)
      }
    },

    lastEventOfEmployee: (state) => {
      return (
        employee_id?: number | null,
        ignoreWorkplan = false,
        withTrashed = false,
        ignoreDuplicateEvents = false,
      ): Event | undefined => {
        const { employee, teamleader } = storeToRefs(useEmployeeStore())
        employee_id =
          employee_id ?? (employee.value?.id || teamleader.value?.id)
        if (!employee_id) {
          return
        }
        const workdays = state.employees[employee_id]?.workdays
        if (!workdays) {
          return
        }
        const lastWorkdayWithEvents = [...workdays]
          .reverse()
          .find((workday) => {
            if (ignoreWorkplan) {
              return (
                workday.events.filter((event) => !event.canBeIgnored()).length >
                0
              )
            }
            return workday.events.length > 0
          })

        if (lastWorkdayWithEvents) {
          const previousEvents = lastWorkdayWithEvents.events.filter(
            (event) =>
              event.occured_at <= DateTime.now().plus({ seconds: 30 }) &&
              (ignoreWorkplan ? !event.canBeIgnored() : true) &&
              (withTrashed ? true : !event.is_deleted),
          )
          if (ignoreDuplicateEvents) {
            let lastEvent = previousEvents[previousEvents.length - 1]
            for (const event of previousEvents.slice().reverse()) {
              if (
                event.task_id === lastEvent.task_id &&
                event.project_id === lastEvent.project_id &&
                event.project_date_id === lastEvent.project_date_id
              ) {
                lastEvent = event
              } else {
                return lastEvent
              }
            }
            return lastEvent
          }
          return previousEvents[previousEvents.length - 1]
        }
      }
    },
    lastEvent(): Event | undefined {
      return this.lastEventOfEmployee()
    },

    lastEventDuration(): Duration {
      return this.lastEventDurationOfEmployee()
    },

    lastTeamEvent: (state): Event | undefined => {
      return state.teamWorkday?.events[state.teamWorkday.events.length - 1]
    },

    teamWorkdayWorkDuration: (state): Duration => {
      const store = useEventStore()
      if (!state.teamWorkday) {
        return Duration.fromMillis(0)
      }
      if (store.lastTeamEvent?.isWorkEvent()) {
        return Duration.fromMillis(state.teamWorkday.work_seconds * 1000).plus(
          store._rawEventDuration(store.lastTeamEvent),
        )
      } else {
        return Duration.fromMillis(state.teamWorkday.work_seconds * 1000)
      }
    },

    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    lastEventDurationOfEmployee: (state) => {
      return (employee_id?: number, ignoreWorkplan = false): Duration => {
        const { employee, teamleader } = storeToRefs(useEmployeeStore())
        employee_id =
          employee_id ?? (employee.value?.id || teamleader.value?.id)
        const store = useEventStore()
        const lastEventOfEmp = store.lastEventOfEmployee(
          employee_id,
          ignoreWorkplan,
        )
        if (!lastEventOfEmp) {
          console.log(
            'trying to calculate lastEventDuration of Employee, but no last event found',
          )
          return Duration.fromMillis(0)
        }
        return store._rawEventDuration(lastEventOfEmp)
      }
    },

    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    lastTaskDurationOfEmployee: (state) => {
      return (employee_id?: number, ignoreWorkplan = false): Duration => {
        const { employee, teamleader } = storeToRefs(useEmployeeStore())
        employee_id =
          employee_id ?? (employee.value?.id || teamleader.value?.id)
        const store = useEventStore()
        const lastEventOfEmp = store.lastEventOfEmployee(
          employee_id,
          ignoreWorkplan,
          false,
          true,
        )
        if (!lastEventOfEmp) {
          console.log(
            'trying to calculate lastEventDuration of Employee, but no last event found',
          )
          return Duration.fromMillis(0)
        }
        return now.value.diff(lastEventOfEmp.occured_at)
      }
    },

    lastTaskDuration(): Duration {
      return this.lastTaskDurationOfEmployee()
    },

    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    _rawEventDuration: (state) => {
      // updates every second
      return (event: Event): Duration => {
        if (event.duration_seconds) {
          return Duration.fromMillis(event.duration_seconds * 1000)
        } else {
          return now.value.diff(event.occured_at)
        }
      }
    },

    lastWorkEventOfEmployee: (state) => {
      return (employee_id?: number): Event | undefined => {
        const firstKey = Object.keys(state.employees)[0]
        const workdays = state.employees[employee_id ?? firstKey]?.workdays
        if (!workdays) {
          return undefined
        }
        const lastWorkdayWithEvents = [...workdays]
          .reverse()
          .find((workday) => workday.events.length > 0)
        if (lastWorkdayWithEvents && lastWorkdayWithEvents.events.length > 1) {
          const reversedEvents = [...lastWorkdayWithEvents?.events].reverse()
          return reversedEvents.find((e) => e.isWorkEvent())
        }
      }
    },
    lastWorkEvent(): Event | undefined {
      return this.lastWorkEventOfEmployee()
    },

    lastTeamWorkEvent(state) {
      if (state.teamWorkday?.events) {
        return [...state.teamWorkday?.events]
          .reverse()
          .find((e) => e.isWorkEvent())
      }
    },
    employeeIsClockedIn() {
      return (employee_id?: number): boolean => {
        const lastEventOfEmp = this.lastEventOfEmployee(employee_id)
        return (
          lastEventOfEmp !== undefined &&
          lastEventOfEmp?.task_id !== Task.id.CLOCKOUT &&
          lastEventOfEmp?.task_id !== Task.id._LOGOUT_DURING_CORE_TIME &&
          lastEventOfEmp?.task_id !== Task.id._ABSENT_DURING_CORE_TIME
        )
      }
    },
    activeEmployeeIsClockedIn(): boolean {
      return this.employeeIsClockedIn()
    },
    atLeastOneTeamMemberIsClockedIn(): boolean {
      if (useAuthStore().isTeamDevice) {
        return useEmployeeStore().clockedInEmployees.length > 0
      }
      return false
    },
    userIsWorkingOfEmployee() {
      return (employee_id?: number): boolean => {
        const lastEventOfEmp = this.lastEventOfEmployee(employee_id)
        return (
          lastEventOfEmp !== undefined &&
          lastEventOfEmp?.task_id !== Task.id.CLOCKOUT &&
          lastEventOfEmp?.task_id !== Task.id.BREAK &&
          lastEventOfEmp?.task_id !== Task.id._LOGOUT_DURING_CORE_TIME &&
          lastEventOfEmp?.task_id !== Task.id._ABSENT_DURING_CORE_TIME
        )
      }
    },
    userIsWorking(): boolean {
      return this.userIsWorkingOfEmployee()
    },

    everythingTransmitted: (state) => {
      const workdays: Array<TWorkday> = []
      for (const [, emp] of Object.entries(state.employees)) {
        workdays.push(...emp.workdays)
      }
      const untransmittedDays = workdays.filter((x) => !workdayIsTransmitted(x))
      return untransmittedDays.length === 0
    },

    todaysFirstEvent() {
      return (eventSignature?: {
        task_id: number
        project_id: number | null
        project_date_id: number | null
      }): Event | undefined => {
        const workday = this.lastWorkdayOfEmployee()
        if (!workday) return
        if (!eventSignature) return workday.events[0]
        return workday.events.find(
          (event) =>
            event.task_id === eventSignature.task_id &&
            event.project_id == eventSignature.project_id &&
            event.project_date_id == eventSignature.project_date_id,
        )
      }
    },
  },
  actions: {
    getEvent(uuid: string) {
      return Object.values(this.$state.employees)
        .flatMap((e) => e.workdays.flatMap((w) => w.events))
        .find((e) => e.uuid === uuid)
    },
    createEvents(
      employees: Employee[],
      task_id: number,
      project_id?: number,
      project_date_id?: number,
      site?: Site,
    ) {
      for (const employee of employees) {
        this.createEvent(
          task_id,
          project_id,
          employee.id,
          project_date_id,
          site,
          false,
        )
        void this.push(employee.id)
      }
    },
    createEvent(
      task_id: number,
      project_id?: number | null,
      employee_id?: number | null,
      project_date_id?: number | null,
      site: Site | null = null,
      pushImmediately = true,
      timeRecordingAction?: TServerEvent.time_recording_action,
    ) {
      // makes sure we cannot create the same event twice in a row
      const lastEvent = this.lastEventOfEmployee(employee_id)
      if (
        task_id === lastEvent?.task_id &&
        project_id == lastEvent?.project_id && // double equal to make sure undefined equals null
        project_date_id == lastEvent?.project_date_id &&
        site?.id == lastEvent?.site?.id
      ) {
        return
      }
      if (Task.serverTaskIds.includes(task_id)) {
        throw new Error(
          'Tried to create a ServerEvent on Device. This is not possible',
        )
      }
      const employeeStore = useEmployeeStore()

      if (!employee_id) {
        employee_id = employeeStore.employee?.id
      }

      if (!employee_id) {
        throw new Error('No employee_id provided when trying to create event')
      }

      if (!site && task_id !== Task.id.BREAK) {
        site = useConfigStore().activeSite ?? null
      }

      //fallback to working time if project is not available anymore
      if (project_id && useProjectStore().getProjectById(project_id) === null) {
        task_id = Task.id.WORK
        project_id = null
        project_date_id = null
      }
      const task = employee_id
        ? useTaskStore().taskOfEmployeeById(employee_id, task_id)
        : null

      const event = new Event(
        task_id,
        project_id,
        employee_id,
        project_date_id,
        undefined,
        null,
        null,
        task?.name,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        timeRecordingAction,
      )
      event.site = site
      now.value = DateTime.now()
      this.$state.isSyncedWithServer = false
      this.register(event)

      if (
        useAuthStore().isTeamDevice &&
        event.employee_id === useEmployeeStore().teamleader?.id
      ) {
        void useLocationStore().handleNewEvent(event)
      } else if (!useAuthStore().isTeamDevice) {
        void useLocationStore().handleNewEvent(event)
      }

      if (
        event.task_id === Task.id.BREAK &&
        useAuthStore().isPersonalDevice &&
        employeeStore.employee?.id === employee_id &&
        employeeStore.employee?.can_use_break_reminder
      ) {
        void useClockReminderStore().scheduleBreakReminder()
      }

      if (
        event.task_id !== Task.id.BREAK &&
        useAuthStore().isPersonalDevice &&
        employeeStore.employee?.id === employee_id &&
        employeeStore.employee?.can_use_break_reminder
      ) {
        void useClockReminderStore().cancelBreakReminders()
      }

      // Cancel clock-in reminder when user clocks in
      if (
        event.task_id === Task.id.CLOCKIN &&
        useAuthStore().isPersonalDevice &&
        employeeStore.employee?.id === employee_id
      ) {
        void useClockReminderStore().cancelClockInReminderForToday()
      }

      // Reschedule clockout reminder on every event (except CLOCKOUT)
      // This adjusts the reminder based on actual work time (excluding breaks)
      if (
        event.task_id !== Task.id.CLOCKOUT &&
        useAuthStore().isPersonalDevice &&
        employeeStore.employee?.id === employee_id &&
        employeeStore.employee?.can_use_clockout_reminder
      ) {
        try {
          const currentWorkDuration =
            this.lastWordayWorkDurationOfEmployee(employee_id)
          const currentWorkMinutes = Math.floor(
            currentWorkDuration.as('minutes'),
          )
          void useClockReminderStore().scheduleClockoutReminder(
            currentWorkMinutes,
          )
        } catch (error) {
          report(error as Error)
        }
      }

      // Cancel clock-out reminder when user clocks out
      if (
        event.task_id === Task.id.CLOCKOUT &&
        useAuthStore().isPersonalDevice &&
        employeeStore.employee?.id === employee_id
      ) {
        void useClockReminderStore().cancelClockOutReminderForToday()
      }

      if (pushImmediately) {
        void this.push(employee_id)
      }
      return event
    },

    register(event: Event) {
      const employee_id = event.employee_id ?? undefined

      const lastEve = this.lastEventOfEmployee(employee_id)
      const lastWday = this.lastWorkdayOfEmployee(employee_id)
      const lastWorkdayIsCompleted =
        lastEve?.task_id === Task.id.CLOCKOUT &&
        lastWday?.date !== DateTime.now().toFormat('yyyy-MM-dd')
      const noWorkSessionsYet = lastWday === undefined
      if (noWorkSessionsYet || lastWorkdayIsCompleted) {
        this._addWorkday(employee_id)
      }
      const currentWorkday = this.lastWorkdayOfEmployee(employee_id) as TWorkday

      if (lastEve) {
        this._updateDurations(lastEve, event, currentWorkday)
      }
      currentWorkday.events.push(event)

      if (useAuthStore().isTeamDevice && event.task_id !== Task.id.CLOCKOUT) {
        const newEventIsDifferentFromLastTeamEvent =
          event.task_id !== this.lastTeamEvent?.task_id ||
          event.project_id !== this.lastTeamEvent?.project_id ||
          event.project_date_id !== this.lastTeamEvent?.project_date_id ||
          event.site?.id !== this.lastTeamEvent?.site?.id
        if (!newEventIsDifferentFromLastTeamEvent) return
        if (!this.$state.teamWorkday) {
          this.$state.teamWorkday = {
            date: DateTime.now().toFormat('yyyy-MM-dd'),
            events: [event],
            work_seconds: 0,
            break_seconds: 0,
            target_seconds: null,
            starts_at: DateTime.now().toFormat('yyyy-MM-dd'),
            ends_at: null,
          }
        } else {
          const lastEvent = this.lastTeamEvent
          if (!lastEvent) return
          this.$state.teamWorkday.events.push(event)
          this._updateDurations(lastEvent, event, this.$state.teamWorkday)
        }
      }
    },

    eventWasSent(eventId: string): boolean {
      const event = Object.values(this.employees)
        .flatMap((e) => e.workdays)
        .flatMap((w) => w.events)
        .find((e) => e.uuid === eventId)
      return !event || Boolean(event.transmitted)
    },

    _addWorkday(employee_id?: number) {
      employee_id = employee_id ?? useEmployeeStore().employee?.id
      if (!employee_id) {
        return
      }
      if (!this.$state.employees[employee_id]) {
        this.$state.employees[employee_id] = createInitialStateOfEmployee()
      }
      const employee = this.$state.employees[employee_id]
      employee.workdays.push({
        date: DateTime.now().toFormat('yyyy-MM-dd'),
        events: [],
        work_seconds: 0,
        break_seconds: 0,
        target_seconds: null,
        starts_at: DateTime.now().toFormat('yyyy-MM-dd'),
        ends_at: null,
        employee_id: employee_id,
      })
    },

    _updateDurations(lastEvent: Event, newEvent: Event, workSession: TWorkday) {
      const lastEventDuration =
        newEvent.occured_at.diff(lastEvent.occured_at).as('seconds') || 0
      lastEvent.duration_seconds = lastEventDuration

      if (
        ![
          Task.id.BREAK,
          Task.id.CLOCKOUT,
          Task.id._ABSENT_DURING_CORE_TIME,
          Task.id._LOGOUT_DURING_CORE_TIME,
        ].includes(lastEvent.task_id)
      ) {
        workSession.work_seconds += lastEventDuration
      }
      if (lastEvent.task_id === Task.id.BREAK) {
        workSession.break_seconds += lastEventDuration
      }
    },

    _applyNewState(
      newState: { [key: string]: tStateOfEmployee },
      employee_id?: number,
    ) {
      /*
        this function makes transfers the occured_at time of the last event of the local state to the last event of the server state
        as long as they have the same task_id and the difference between the occured_at times is less than 1 minute
        This mitigates the rounding problem of the occured_at time on the server
      */
      const lastEventOfEmpLocal = this.lastEventOfEmployee(employee_id)
      const lastEventOfEmpLocalOccuredAt = lastEventOfEmpLocal?.occured_at
      const lastEventOfEmpLocalTaskId = lastEventOfEmpLocal?.task_id
      if (!employee_id) {
        this.$state.employees = newState
      } else {
        this.$state.employees[employee_id] = newState[employee_id]
      }
      const lastEventOfEmpServer = this.lastEventOfEmployee(employee_id)
      if (
        lastEventOfEmpServer &&
        lastEventOfEmpLocalOccuredAt &&
        lastEventOfEmpLocalTaskId === lastEventOfEmpServer?.task_id &&
        Math.abs(
          lastEventOfEmpLocalOccuredAt
            ?.diff(lastEventOfEmpServer.occured_at)
            .toMillis(),
        ) < 60000
      ) {
        lastEventOfEmpServer.occured_at = lastEventOfEmpLocalOccuredAt
      }
    },
    async sync(employee_id?: number) {
      try {
        if (!employee_id) {
          for (const [employee_id] of Object.entries(this.$state.employees)) {
            await this.push(parseInt(employee_id))
          }
        } else {
          await this.push(employee_id)
        }

        await this.pull(employee_id)
        this.calculateEventDurations(employee_id)
      } catch (error) {
        console.error(error)
      }
    },

    async push(employee_id: number) {
      //if employee_id undefined push for all employees
      let workdays: Array<TWorkday> = []
      if (employee_id) {
        workdays = this.$state.employees[employee_id].workdays
      } else {
        for (const [, emp] of Object.entries(this.$state.employees)) {
          workdays.push(...emp.workdays)
        }
      }

      const eventsToSync: Array<Event> = workdays
        .map((workday) => workday.events)
        .flat()
        .filter((event) => !event.transmitted)
      if (eventsToSync.length === 0) return
      const payload: Array<TServerEvent> = eventsToSync.map((event) =>
        event.getPayload(),
      )

      await EventsService.storeEvents(payload)
        .then(() => {
          console.log('TRANSMITTED SUCCESSFULLY', payload)
          for (const event of eventsToSync) {
            event.transmitted = true
          }
          this.$state.isSyncedWithServer = true
        })
        .catch(async (error: ApiError) => {
          if (error.status === 401) await useAuthStore().logout()
          //else throw new Error(error.message)
          this.$state.isSyncedWithServer = false
        })
    },

    async pull(employee_id?: number) {
      /**
       * Pulls the employees workday from the server and updates the local state
       */

      // in order to avoid problems regarding team devices
      // we are never pulling events for them
      // why:
      // ( an employee can be assigned to multiple team devices,
      // since the devices sync, clocking on a "wrong" device will affect employees it should not)

      if (useAuthStore().device_type === DeviceType.TEAM) {
        this.$state.isSyncedWithServer = true
        return
      }

      if (!this.everythingTransmitted) {
        /*
        this condition should mitigate race conditions where a push is still in progress or not triggered yet
        and a pull would overwrite the local state with an outdated server state
        */
        console.warn(
          'trying to pull eventStore but not everything is transmitted, stopping pull.',
        )
        return
      }
      const today = DateTime.now().toFormat('dd.MM.yyyy')
      let past = DateTime.now().minus({ days: 6 }).toFormat('dd.MM.yyyy')
      if (useAuthStore().device_type !== DeviceType.PERSONAL) {
        past = DateTime.now().toFormat('dd.MM.yyyy')
      }

      const response = await handleRequest(
        WorkdaysService.getWorkdays(past, today, employee_id),
        60000,
      )
      const data = response?.data
      if (!data) {
        this.$state.isSyncedWithServer = false
        return
      }

      console.log('EventStore pulled data from server')

      const newState: { [key: string]: tStateOfEmployee } = {}

      for (const workday of data ?? []) {
        const dateString = workday.date ?? ''
        const events = workday.events.map((event) =>
          Event.fromServerObject(event),
        )
        if (!newState[workday.employee_id ?? 0]) {
          newState[workday.employee_id ?? 0] = createInitialStateOfEmployee()
        }
        newState[workday.employee_id ?? 0].workdays.push({
          date: dateString,
          events: events,
          work_seconds: workday.work_seconds,
          break_seconds: workday.break_seconds,
          target_seconds: workday.target_seconds ?? 0,
          absence_seconds: workday.absence_seconds,
          absence_categories: workday.absence_categories,
          starts_at: workday.starts_at as string,
          ends_at: workday.ends_at ?? null,
          employee_id: workday.employee_id ?? 0,
        })
      }
      // in case the last local event is newer than the last server event, add the local event to the events received from the server (newState)
      // this is necessary because a pull can be triggered by external events (such as push-notifications or appState-changes)
      // and the local event might not be transmitted yet, leading to state inconsistencies when it is overwritten by the (at this point) outdated server state
      for (const [employeeId, newStateOfEmployee] of Object.entries(newState)) {
        const lastLocalEvent = this.lastEventOfEmployee(parseInt(employeeId))
        const lastServerWorkday =
          newStateOfEmployee.workdays[newStateOfEmployee.workdays.length - 1]
        const lastServerEvent =
          lastServerWorkday.events[lastServerWorkday?.events.length - 1]
        const lastLocalEventIsNewerThanLastServerEvent =
          lastLocalEvent &&
          lastServerEvent &&
          lastLocalEvent.occured_at > lastServerEvent.occured_at
        const lastLocalEventOriginatesFromDevice = !lastLocalEvent?.id

        if (
          lastLocalEventIsNewerThanLastServerEvent &&
          lastLocalEventOriginatesFromDevice
        ) {
          if (
            lastLocalEvent.task_id !== lastServerEvent.task_id ||
            lastLocalEvent.project_id !== lastServerEvent.project_id ||
            lastLocalEvent.project_date_id !== lastServerEvent.project_date_id
          ) {
            lastServerWorkday.events.push(lastLocalEvent)
          }

          // in case the server does not have any events for this workday yet we will keep the local event and add it to the server state
          // unless the local event is older than 1 minute, in which case the server should have the event already and might have deleted it (correction)
        } else if (
          !lastServerEvent &&
          lastLocalEvent &&
          lastLocalEventOriginatesFromDevice &&
          lastLocalEvent.occured_at > DateTime.now().minus({ minutes: 1 })
        ) {
          lastServerWorkday.events.push(lastLocalEvent)
        }
      }
      this._applyNewState(newState, employee_id)
      this.$state.isSyncedWithServer = true
      if (
        useAuthStore().isPersonalDevice &&
        this.lastEvent?.site?.id &&
        this.lastEvent.site.id !== useConfigStore().activeSite?.id
      ) {
        useConfigStore().setActiveSite(this.lastEvent.site.id)
      }
    },

    calculateEventDurations(employee_id?: number) {
      //if employee_id undefined calculate for all employees
      let workdays: Array<TWorkday> = []
      if (employee_id) {
        workdays = this.$state.employees[employee_id].workdays
      } else {
        for (const [, emp] of Object.entries(this.$state.employees)) {
          workdays.push(...(emp.workdays ?? []))
        }
      }
      for (const workday of workdays) {
        for (const [index, event] of workday.events.entries()) {
          if (index === 0) return
          const previousEvent = workday.events[index - 1]
          const duration = event.occured_at
            .diff(previousEvent.occured_at)
            .toObject().seconds
          event.duration_seconds = duration || 0
        }
      }
    },
    hydrate(state: string) {
      this.$state = JSON.parse(state, (key, value) => {
        if (key === 'events') {
          return value.map((eventString: TDehydratedEvent) =>
            Event.fromJSON(eventString),
          )
        } else {
          return value
        }
      })
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useEventStore, import.meta.hot))
}

export { useEventStore }
export type { TWorkday }
